import cloneDeep from 'lodash/cloneDeep';

import { Injectable, OnDestroy } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, Subject, of } from 'rxjs';

import { TUser } from '../../user/user.model';
import {
  ClearPreferences,
  SetPreferences,
  UpdatePreferences,
  UpdateWorkspaceExportPreferences,
} from '../preferences.actions';
import { TPreferences, TWorkspaceLayout, TWorkspacePreferences } from '../preferences.model';

import { ResponseErrorService } from '../../errors/response-error.service';
import { UserService } from '../../user/user.service';

import { SET_PREFERENCES } from '../preferences.store';
import { processUserPreferencesResponse } from './process-user-preferences-response';
import { processPreferencesResponse } from './update-preferences-utils';

import { PreferencesApiProviderService } from '@core/api';
import {
  catchError,
  filter,
  finalize,
  map,
  shareReplay,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { getUserAuth } from 'src/app/core/http/user-auth';
import { logErrorInSentry } from '../../errors/response-error';
import { QuickFilterService } from '../../filters/filters-api/search/quick-filter-service';
import { getDefaultQuickFilters } from '../../filters/utils/get-default-quick-filters';
import { TSavedView } from '../../saved-views/models/saved-view.model';
import { TSavedViews } from '../../saved-views/models/saved-views.model';
import { SavedViewsService } from '../../saved-views/saved-views.service';
import { generateDefaultColumns } from '../../site/site-table/columns/columns';
import { TableColumnsService } from '../../site/site-table/columns/table-columns.service';

const STORED_USER_IS_NOT_SIGNED_IN_USER_MESSAGE =
  'Preferences read from the store for a user who is not the signed-in user';

@Injectable({
  providedIn: 'root',
})
export class PreferencesService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly favouritesChanged$ = new Subject<void>();
  readonly favouritesChange$ = this.favouritesChanged$.asObservable();

  private userPreferencesFetched: boolean;

  private userPreferencesPromise: Observable<TPreferences> = null;

  private preferences$: Observable<TPreferences>;
  private preferences: TPreferences;

  private fetchExportPreferences$: Observable<TWorkspacePreferences>;

  constructor(
    private store: Store<{
      user: TUser;
      preferences: TPreferences;
    }>,
    private responseErrorService: ResponseErrorService,
    private userService: UserService,
    private preferencesApiProviderService: PreferencesApiProviderService,
    private savedViewsService: SavedViewsService,
    private tableColumnsService: TableColumnsService,
    private quickFilterService: QuickFilterService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.preferences$ = this.store.pipe(select(EStore.PREFERENCES));

    this.preferences$.subscribe((preferences) => {
      this.preferences = cloneDeep(preferences);

      SET_PREFERENCES(this.preferences);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  getPreferences(): TPreferences {
    return this.preferences;
  }

  clearPreferences(): void {
    this.userPreferencesPromise = null;
    this.fetchExportPreferences$ = null;

    this.store.dispatch(new ClearPreferences());
  }

  fetchPreferences({
    refetch = false,
    forceFetch = false,
  }: {
    refetch?: boolean;
    forceFetch?: boolean;
  } = {}): Observable<TPreferences> {
    if (this.featureFlagService.getFlag(EFeatureFlag.GET_CURRENT_USER_ENDPOINT)) {
      return this.waitForCurrentUserPreferences();
    }

    const user = this.userService.getUser();

    if (!this.userPreferencesFetched || refetch || forceFetch || !this.userPreferencesPromise) {
      this.userPreferencesPromise = this.generateUserPreferencesPromise(user);
    }

    return this.userPreferencesPromise;
  }

  private waitForCurrentUserPreferences(): Observable<TPreferences> {
    this.logIfStoredUserIsNotSignedInUser();

    return this.store.pipe(
      select(EStore.USER),
      filter((user) => Boolean(user?.userId)),
      take(1),
      map(() => this.preferences),
    );
  }

  private logIfStoredUserIsNotSignedInUser(): void {
    const storedUserEmail = this.userService.getUser()?.email;

    if (!storedUserEmail || storedUserEmail === getUserAuth()?.email) {
      return;
    }

    logErrorInSentry({ message: STORED_USER_IS_NOT_SIGNED_IN_USER_MESSAGE });
  }

  generateUserPreferencesPromise(user: TUser): Observable<TPreferences> {
    return this.getUserPreferences(user.userId, this.preferences).pipe(
      tap((response) => {
        this.userPreferencesFetched = true;

        this.store.dispatch(
          new SetPreferences(processUserPreferencesResponse(response, this.preferences)),
        );
      }),
      shareReplay(),
      catchError((error) => {
        this.userPreferencesFetched = false;

        return this.responseErrorService.handleRequestError(
          error,
          'Failed to fetch user preferences',
        );
      }),
    );
  }

  updatePreferences(newPreferences: Partial<TPreferences>): Observable<TPreferences | null> {
    const user = this.userService.getUser();

    return this.preferencesApiProviderService
      .updateUserPreferences(user.userId, newPreferences)
      .pipe(
        map((response) => {
          const preferences = processPreferencesResponse(response, this.preferences);

          this.store.dispatch(new UpdatePreferences(preferences));
          SET_PREFERENCES(preferences);

          return preferences;
        }),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to update user preferences'),
        ),
      );
  }

  updateExportPreferences(newPreferences: TWorkspacePreferences, workspaceId: string = ''): void {
    this.preferencesApiProviderService
      .updateExportPreferences(workspaceId, newPreferences)
      .pipe(
        tap((response) => {
          this.store.dispatch(
            new UpdateWorkspaceExportPreferences({
              workspaceId,
              preferences: { ...response },
            }),
          );
        }),
        catchError((error) => {
          this.responseErrorService.logRequestError(error);

          return of(null);
        }),
      )
      .subscribe();
  }

  savePreferences(workspaceId: string): void {
    const newColumns = this.tableColumnsService.getColumns();

    this.savedViewsService.tryUpdateActiveView(workspaceId, newColumns);
  }

  private getUserPreferences(
    userId: string,
    oldPreferences: TPreferences,
  ): Observable<TPreferences> {
    return this.preferencesApiProviderService
      .getUserPreferences(userId)
      .pipe(map((response) => processUserPreferencesResponse(response, oldPreferences)));
  }

  fetchExportPreferences(_workspaceId: string): Observable<TWorkspacePreferences> {
    if (!this.fetchExportPreferences$) {
      this.fetchExportPreferences$ = this.preferencesApiProviderService
        .getExportPreferences(_workspaceId)
        .pipe(
          map((response) => response || {}),
          finalize(() => {
            this.fetchExportPreferences$ = null;
          }),
        );
    }

    return this.fetchExportPreferences$;
  }

  getCurrentWorkspaceLayout({
    savedViews,
    selectedViewId,
  }: {
    savedViews?: TSavedViews;
    selectedViewId?: string;
  }): TWorkspaceLayout {
    if (selectedViewId) {
      const view = this.savedViewsService.getView(selectedViewId);
      return this.generateLayoutFromSavedView(view);
    } else if (savedViews?.personal?.defaultViewId) {
      const view = this.savedViewsService.getView(savedViews.personal.defaultViewId);
      return this.generateLayoutFromSavedView(view);
    } else if (savedViews?.shared?.sharedDefaultViewId) {
      const view = this.savedViewsService.getView(savedViews.shared.sharedDefaultViewId);
      return this.generateLayoutFromSavedView(view);
    } else {
      const view: TWorkspaceLayout = {
        columns: generateDefaultColumns(),
        collapsedGroups: [],
        quickFilters: {
          quick: getDefaultQuickFilters(),
          advanced: { filters: [], operator: 'AND' },
        },
      };

      this.quickFilterService.setFilters({
        quick: view.quickFilters.quick,
        advanced: view.quickFilters.advanced.filters,
        operator: view.quickFilters.advanced.operator,
      });

      return view;
    }
  }

  removeFavouriteWorkspace(workspaceId: string): Observable<TPreferences | null> {
    const favouriteWorkspaceIds = this.preferences.favouriteWorkspaceIds.filter(
      (id) => id !== workspaceId,
    );

    return this.updatePreferences({ favouriteWorkspaceIds }).pipe(
      tap(() => this.favouritesChanged$.next()),
    );
  }

  addFavouriteWorkspace(workspaceId: string): Observable<TPreferences | null> {
    const favouriteWorkspaceIds = [...this.preferences.favouriteWorkspaceIds, workspaceId];

    return this.updatePreferences({ favouriteWorkspaceIds }).pipe(
      tap(() => this.favouritesChanged$.next()),
    );
  }

  private generateLayoutFromSavedView(view: TSavedView): TWorkspaceLayout {
    const layout: Partial<TWorkspaceLayout> = {};

    if (view && view.columns) {
      layout.columns = [...cloneDeep(view.columns)];

      layout.quickFilters = cloneDeep(view.quickFilters);
      layout.collapsedGroups = view.collapsedGroups;

      this.quickFilterService.setFilters({
        quick: view.quickFilters.quick,
        advanced: view.quickFilters.advanced.filters,
        operator: view.quickFilters.advanced.operator,
      });
    }
    return layout as TWorkspaceLayout;
  }
}
